import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, UploadCloud, HelpCircle, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getSiteColor } from "@/lib/siteColors";
import DashboardCharts from "@/components/hfacs/DashboardCharts";
import { runTour, dashboardTourSteps } from "@/lib/tours";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canDelete = user?.role === "Administrateur national" || user?.role === "Administrateur Site";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([axios.get(`${API}/hfacs`), axios.get(`${API}/sites`)]);
      setAnalyses(a.data);
      setSites(s.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => runTour("dashboard", dashboardTourSteps), 700);
    return () => clearTimeout(t);
  }, []);

  const siteCode = (idSite) => sites.find((s) => s.ID_SITE === idSite)?.CODE_SITE || idSite;

  const confirmDeleteAnalysis = async () => {
    if (!analysisToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/hfacs/${analysisToDelete.ID_HFACS}`);
      toast.success(`${analysisToDelete.ID_HFACS} supprimée (dossier Drive mis à la corbeille).`);
      setAnalysisToDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression de l'analyse.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 data-testid="dashboard-welcome" className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl mb-2">
              Bienvenue, {user?.prenom || user?.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              Rôle : <span className="text-foreground font-medium">{user?.role}</span>
              {user?.site ? <> · Site : <span className="text-foreground font-medium">{siteCode(user.site)}</span></> : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => runTour("dashboard", dashboardTourSteps, { force: true })}
              data-testid="dashboard-help-button"
              title="Revoir la visite guidée"
              className="p-2 rounded-sm border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            {user?.role === "Administrateur national" && (
              <Link to="/hfacs/importer">
                <Button data-testid="dashboard-import-analysis-button" variant="outline" className="gap-2 border-accent/40 text-accent hover:bg-accent/10">
                  <UploadCloud className="h-4 w-4" />
                  Importer une analyse
                </Button>
              </Link>
            )}
            <Link to="/hfacs/nouveau">
              <Button data-testid="dashboard-new-analysis-button" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <Plus className="h-4 w-4" />
                Nouvelle analyse
              </Button>
            </Link>
          </div>
        </div>

        <h2 className="font-heading uppercase font-semibold text-sm text-muted-foreground mb-3">Statistiques</h2>
        <DashboardCharts />

        <h2 className="font-heading uppercase font-semibold text-sm text-muted-foreground mb-3">Analyses HFACS</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : analyses.length === 0 ? (
          <p data-testid="dashboard-empty-state" className="text-sm text-muted-foreground py-8">Aucune analyse pour le moment. Créez la première analyse HFACS.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analyses-grid">
            {analyses.map((a) => {
              const color = getSiteColor(siteCode(a.SITE));
              return (
                <Link
                  key={a.ID_HFACS}
                  to={`/hfacs/${a.ID_HFACS}`}
                  data-testid={`analysis-card-${a.ID_HFACS}`}
                  className={`relative block bg-card border border-border border-l-4 ${color.border} rounded-md p-4 hover:-translate-y-0.5 transition-transform`}
                >
                  {canDelete && (
                    <button
                      data-testid={`analysis-delete-trigger-${a.ID_HFACS}`}
                      title="Supprimer cette analyse"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAnalysisToDelete(a); }}
                      className="absolute top-3 right-3 z-10 p-1 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <div className="flex items-center justify-between mb-2 pr-6">
                    <span className={`font-mono text-[10px] uppercase ${color.text}`}>{siteCode(a.SITE)}</span>
                    <div className="flex items-center gap-1.5">
                      {a.ORIGINE === "Importée" && (
                        <span data-testid={`analysis-imported-badge-${a.ID_HFACS}`} className="font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm border border-accent/40 bg-accent/10 text-accent">
                          Importée
                        </span>
                      )}
                      <span className="font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm border border-border bg-secondary text-secondary-foreground">{a.STATUT}</span>
                    </div>
                  </div>
                  <p className="font-heading font-semibold text-sm mb-1">{a.ID_HFACS}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{a.TYPE_EVENEMENT}</p>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={!!analysisToDelete} onOpenChange={(open) => !open && setAnalysisToDelete(null)}>
        <AlertDialogContent data-testid="delete-analysis-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {analysisToDelete?.ID_HFACS} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible : les faits, facteurs, liens causaux, chemins critiques et actions HFIX de cette analyse seront définitivement supprimés de la base, et son dossier Drive sera mis à la corbeille.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-analysis-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-analysis-confirm"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmDeleteAnalysis(); }}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
